/**
 * 版本号：v2.4.1
 * 变更记录：
 * - v2.4.1: BasicInfo 新增字段 propertyFee（物业费，元/月），参与租金合计但不影响租金抽成计算
 * - v2.4.0: 性能优化 - 实施xlsx库懒加载，减少初始包体积
 * - v2.3.0: 添加装修成本上下限约束机制
 * - v2.2.0: 优化人工费用计算逻辑
 * - v2.1.0: 添加品牌管理费动态计算规则
 * - v2.0.0: 重构数据模型，支持更复杂的计算规则
 * - v1.1.0: 添加装修成本上下限约束功能
 * - v1.0.0: 初始版本，包含基础数据模型定义
 */

import { sortProvincesByFirstLetter } from './helpers';
import { maskSensitiveData } from './consoleSecurity';

export const UNRECOVERABLE_PAYBACK_PERIOD = 999;

export interface OperatingCostRatios {
  waterElectricityRatio: number;
  otherStoreCostsRatio: number;
  brandManagementFeeRatio: number;
  laborCostsRatio: number;
}

export interface WaterElectricityLimits {
  min?: number;
  max?: number;
}

export interface OperatingCostLimits {
  waterElectricity?: WaterElectricityLimits;
  otherStoreCosts?: {
    rules: string;
  };
  brandManagementFee?: {
    rules: CostRule[];
  };
}

let xlsxModule: any = null;

const loadXlsxModule = async () => {
  if (!xlsxModule) {
    xlsxModule = await import('xlsx');
  }
  return xlsxModule;
};

/**
 * 基础信息类型
 */
export interface BasicInfo {
  province: string;
  city: string;
  district: string;
  cityTier: string;
  businessArea: string;
  area: number;
  estimatedRevenue: number;
  fixedRent: number;
  rentPercentage: number;
  /** 物业费（元/月），可选输入，参与租金合计 */
  propertyFee?: number;
}

// 定义月度运营费用类型
export interface OperatingCosts {
  waterElectricity: number;
  otherStoreCosts: number;
  brandManagementFee: number;
  laborCosts: number;
  rent: number;
  total: number;
  /** 当用户手动输入水电费时，记录该覆盖值；存在则优先使用 */
  waterElectricityManual?: number | null;
}

// 定义建店成本类型
export interface InitialCosts {
  franchiseFee: number;
  brandUsageFee: number;
  designFee: number;
  storeServiceFee: number;
  equipmentCost: number;
  decorationCost: number;
  total: number;
}

// 定义装修成本规则类型
export interface DecorationCostRule {
  minArea: number;
  maxArea: number | null;
  costPerSquareMeter: number;
}

// 定义测算结果类型
export interface CalculationResults {
  actualEstimatedRevenue: number;
  estimatedGrossMargin: number;
  monthlyProfitNoDepreciation: number;
  monthlyProfitWithDepreciation: number;
  paybackPeriod: number;
  rent36Month: number;
  minRevenueFor36MonthPayback: number;
}

// 定义费用规则类型
export interface CostRule {
  minRevenue?: number;
  maxRevenue?: number;
  fixedAmount?: number;
  percentage?: number;
  excessPercentage?: number;
  thresholdAmount?: number;
  formula?: string;
}

// 定义人力成本计算规则类型
export interface LaborCostRule {
  cityTier: string;
  minValue: number;
  averageSalary: number;
  thresholdRevenue: number;
  thresholdLabor: number;
  increaseRate: number;
}

// 定义筛选后的数据类型
export interface FilteredData {
  provinces: string[];
  cities: Record<string, string[]>;
  districts: Record<string, string[]>;
  cityTiers: Record<string, string>;
  districtTiers: Record<string, string>;
  businessAreas: string[];
  operatingCostRatios: Record<string, OperatingCostRatios>;
  operatingCostLimits?: OperatingCostLimits;
  initialCostsData?: {
    franchiseFee: number;
    brandUsageFee: number;
    designFee: number;
    storeServiceFee: number;
    equipmentCost: number;
    decorationCostRules: DecorationCostRule[];
    decorationCostMaxLimit?: number;
    decorationCostMinLimit?: number;
  };
  laborCostRules?: LaborCostRule[];
}     

// 定义商圈类型与毛利率的映射
export const businessAreaGrossMarginRates: Record<string, number> = {
  '常规': 0.52,
 // '景区': 0.52,
  '火车站': 0.70
};

// 根据商圈类型获取毛利率
export const getGrossMarginRateByBusinessArea = (businessArea: string): number => {
  return businessAreaGrossMarginRates[businessArea] || 0.60; // 默认返回60%
};

/**
 * 加载Excel数据
 * 性能优化：使用懒加载的xlsx模块，减少初始包体积
 * @param file Excel文件或ArrayBuffer
 * @returns 解析后的Excel数据
 */
export const loadExcelData = async (file: File | ArrayBuffer): Promise<any> => {
  try {
    // 懒加载xlsx模块
    const { read, utils } = await loadXlsxModule();

    let arrayBuffer: ArrayBuffer;

    if (file instanceof File) {
      arrayBuffer = await file.arrayBuffer();
    } else {
      arrayBuffer = file;
    }

    const workbook = read(arrayBuffer);
    const result: any = {};

    // 处理每个工作表
    workbook.SheetNames.forEach((sheetName: string) => {
      const worksheet = workbook.Sheets[sheetName];
      result[sheetName] = utils.sheet_to_json(worksheet);
    });

    return result;
  } catch (error) {
    console.error('加载Excel数据失败:', error);
    throw error;
  }
};

/**
 * 解析装修成本规则字符串
 * @param ruleStr 规则字符串，如 ">=0,<60,3800"
 * @returns 解析后的规则对象
 */
const parseDecorationCostRule = (ruleStr: string): DecorationCostRule | null => {
  try {
    // 解析规则字符串
    const parts = ruleStr.split(',');
    
    if (parts.length !== 3 && parts.length !== 2) {
      console.warn('无效的装修成本规则格式:', ruleStr);
      return null;
    }
    
    let minArea = 0;
    let maxArea: number | null = null;
    let costPerSquareMeter = 0;
    
    // 解析最小面积
    if (parts[0].startsWith('>=')) {
      minArea = parseFloat(parts[0].substring(2));
    } else {
      console.warn('无效的最小面积格式:', parts[0]);
      return null;
    }
    
    // 解析最大面积（如果有）
    if (parts.length === 3) {
      if (parts[1].startsWith('<')) {
        maxArea = parseFloat(parts[1].substring(1));
      } else {
        console.warn('无效的最大面积格式:', parts[1]);
        return null;
      }
      costPerSquareMeter = parseFloat(parts[2]);
    } else {
      // 如果只有两部分，则没有最大面积限制
      costPerSquareMeter = parseFloat(parts[1]);
    }
    
    return {
      minArea,
      maxArea,
      costPerSquareMeter
    };
  } catch (error) {
    console.error('解析装修成本规则失败:', error);
    return null;
  }
};

/**
 * 解析费用规则字符串
 * @param ruleStr 规则字符串，如 "x>=8000,x<=20000"
 * @returns 解析后的规则对象
 */
const parseCostLimitRule = (ruleStr: string): { min?: number; max?: number } | null => {
  try {
    if (!ruleStr || typeof ruleStr !== 'string') {
      return null;
    }

    const result: { min?: number; max?: number } = {};
    
    // 分割多个规则
    const parts = ruleStr.split(',');
    
    for (const part of parts) {
      const trimmedPart = part.trim();
      
      // 解析最小值 x>=8000
      if (trimmedPart.includes('>=')) {
        const minValue = parseFloat(trimmedPart.split('>=')[1]);
        if (!isNaN(minValue)) {
          result.min = minValue;
        }
      } 
      // 解析最小值 x>8000
      else if (trimmedPart.includes('>')) {
        const minValue = parseFloat(trimmedPart.split('>')[1]);
        if (!isNaN(minValue)) {
          result.min = minValue;
        }
      }
      // 解析最大值 x<=20000
      else if (trimmedPart.includes('<=')) {
        const maxValue = parseFloat(trimmedPart.split('<=')[1]);
        if (!isNaN(maxValue)) {
          result.max = maxValue;
        }
      }
      // 解析最大值 x<20000
      else if (trimmedPart.includes('<')) {
        const maxValue = parseFloat(trimmedPart.split('<')[1]);
        if (!isNaN(maxValue)) {
          result.max = maxValue;
        }
      }
    }
    
    return result;
  } catch (error) {
    console.error('解析费用规则失败:', error);
    return null;
  }
};

/**
 * 解析品牌管理费规则字符串
 * @param ruleStr 规则字符串，如 "<130000,x=0" 或 ">=130000,<200000,x=x*3%"
 * @returns 解析后的规则对象
 */
const parseBrandManagementFeeRule = (ruleStr: string): CostRule | null => {
  try {
    if (!ruleStr || typeof ruleStr !== 'string') {
      return null;
    }

    const rule: CostRule = {};
    
    // 分割规则部分和计算公式部分
    const parts = ruleStr.split(',');
    
    // 解析规则条件（可能有多个条件）
    for (let i = 0; i < parts.length - 1; i++) {
      const condition = parts[i].trim();
      
      // 解析最小营业额 >=130000
      if (condition.startsWith('>=')) {
        rule.minRevenue = parseFloat(condition.substring(2));
      } 
      // 解析最小营业额 >130000
      else if (condition.startsWith('>')) {
        rule.minRevenue = parseFloat(condition.substring(1));
      }
      // 解析最大营业额 <200000
      else if (condition.startsWith('<')) {
        rule.maxRevenue = parseFloat(condition.substring(1));
      }
      // 解析最大营业额 <=200000
      else if (condition.startsWith('<=')) {
        rule.maxRevenue = parseFloat(condition.substring(2));
      }
    }
    
    // 解析计算公式部分 x=0 或 x=x*3% 或 x=6000+(x-200000)*6%
    const formulaPart = parts[parts.length - 1].trim();
    if (formulaPart.startsWith('x=')) {
      const formula = formulaPart.substring(2);
      rule.formula = formula;
      
      // 简单公式：x=0
      if (formula === '0') {
        rule.fixedAmount = 0;
      }
      // 百分比公式：x=x*3%
      else if (formula.includes('x*') && formula.includes('%')) {
        const percentStr = formula.split('*')[1].replace('%', '');
        rule.percentage = parseFloat(percentStr) / 100;
      }
      // 复杂公式：x=6000+(x-200000)*6%
      else if (formula.includes('+') && formula.includes('(') && formula.includes(')') && formula.includes('*') && formula.includes('%')) {
        const fixedPart = formula.split('+')[0];
        rule.fixedAmount = parseFloat(fixedPart);
        
        const excessPart = formula.split('(')[1].split(')')[0];
        if (excessPart.startsWith('x-')) {
          rule.thresholdAmount = parseFloat(excessPart.substring(2));
        }
        
        const percentPart = formula.split('*')[1].replace('%', '');
        rule.excessPercentage = parseFloat(percentPart) / 100;
      }
    }
    
    return rule;
  } catch (error) {
    console.error('解析品牌管理费规则失败:', error, ruleStr);
    return null;
  }
};

/**
 * 从Excel数据中提取筛选后的数据
 * @param excelData Excel数据
 * @returns 筛选后的数据
 */
export const extractFilteredData = (excelData: any): FilteredData => {
  const result: FilteredData = {
    provinces: [],
    cities: {},
    districts: {},
    cityTiers: {},
    districtTiers: {},
    businessAreas: [],
    operatingCostRatios: {},
    operatingCostLimits: {},
    initialCostsData: {
      franchiseFee: 0,
      brandUsageFee: 0,
      designFee: 0,
      storeServiceFee: 0,
      equipmentCost: 0,
      decorationCostRules: []
    },
    laborCostRules: []
  };
  
  try {
    // 检查是否有基础信息工作表
    if (!excelData || !excelData['基础信息']) {
      console.error('Excel数据中缺少基础信息工作表');
      return result;
    }
    
    const baseInfoSheet = excelData['基础信息'];
    
    // 提取省份、城市、区县信息
    const provinceSet = new Set<string>();
    const cityMap: Record<string, Set<string>> = {};
    const districtMap: Record<string, Set<string>> = {};
    const cityTierMap: Record<string, string> = {};
    const districtTierMap: Record<string, string> = {};
    const businessAreaSet = new Set<string>();
    
    // 用于保持Excel原始顺序的映射
    const provinceOrderMap: Record<string, number> = {};
    const cityOrderMap: Record<string, Record<string, number>> = {};
    const districtOrderMap: Record<string, Record<string, number>> = {};
    
    // 遍历基础信息表中的每一行
    baseInfoSheet.forEach((row: any, index: number) => {
      // 提取省份
      if (row['省'] && typeof row['省'] === 'string') {
        provinceSet.add(row['省']);
        // 记录省份在Excel中的顺序
        if (provinceOrderMap[row['省']] === undefined) {
          provinceOrderMap[row['省']] = index;
        }
        
        // 提取城市
        if (row['市'] && typeof row['市'] === 'string') {
          if (!cityMap[row['省']]) {
            cityMap[row['省']] = new Set<string>();
            cityOrderMap[row['省']] = {};
          }
          cityMap[row['省']].add(row['市']);
          
          // 记录城市在Excel中的顺序
          if (cityOrderMap[row['省']][row['市']] === undefined) {
            cityOrderMap[row['省']][row['市']] = index;
          }
          
          // 提取城市梯队
          if (row['城市梯队'] && typeof row['城市梯队'] === 'string') {
            cityTierMap[row['市']] = row['城市梯队'];
          }
          
          // 提取区县
          if (row['区县'] && typeof row['区县'] === 'string') {
            if (!districtMap[row['市']]) {
              districtMap[row['市']] = new Set<string>();
              districtOrderMap[row['市']] = {};
            }
            districtMap[row['市']].add(row['区县']);
            
            // 记录区县在Excel中的顺序
            if (districtOrderMap[row['市']][row['区县']] === undefined) {
              districtOrderMap[row['市']][row['区县']] = index;
            }
            
            // 提取区县梯队，使用"省-市-区县"格式作为键
            if (row['城市梯队'] && typeof row['城市梯队'] === 'string') {
              const fullDistrictKey = `${row['省']}-${row['市']}-${row['区县']}`;
              districtTierMap[fullDistrictKey] = row['城市梯队'];
            }
          }
        }
      }
      
      // 提取商圈类型
      if (row['商圈类型'] && typeof row['商圈类型'] === 'string') {
        businessAreaSet.add(row['商圈类型']);
      }
    });
    
    // 转换Set为数组，按Excel中的顺序排序
    result.provinces = sortProvincesByFirstLetter(Array.from(provinceSet));
    
    // 转换城市Map，按Excel中的顺序排序
    Object.keys(cityMap).forEach(province => {
      result.cities[province] = Array.from(cityMap[province]).sort((a, b) => {
        return cityOrderMap[province][a] - cityOrderMap[province][b];
      });
    });
    
    // 转换区县Map，按Excel中的顺序排序
    Object.keys(districtMap).forEach(city => {
      result.districts[city] = Array.from(districtMap[city]).sort((a, b) => {
        return districtOrderMap[city][a] - districtOrderMap[city][b];
      });
    });
    
    // 设置城市梯队
    result.cityTiers = cityTierMap;
    
    // 设置区县梯队
    result.districtTiers = districtTierMap;
    
    // 设置商圈类型，按字母顺序排序
    result.businessAreas = Array.from(businessAreaSet).sort();
    
    // 提取月度营运费用比例信息（如果有）
    if (excelData['月度营运费用'] && excelData['月度营运费用'].length > 0) {
      const operatingCostsSheet = excelData['月度营运费用'];
      const costRatios: Record<string, OperatingCostRatios> = {};
      const costLimits: OperatingCostLimits = {};
      
      // 假设只有一行数据，取第一行
      const ratioRow = operatingCostsSheet[0];
      
      costRatios['default'] = {
        waterElectricityRatio: ratioRow['水电费'] || 0,
        otherStoreCostsRatio: ratioRow['门店其他费用'] || 0,
        brandManagementFeeRatio: ratioRow['品牌管理费'] || 0,
        laborCostsRatio: ratioRow['人工+福利'] || 0
      };
      
      result.operatingCostRatios = costRatios;
      
      // 提取费用限制规则
      if (operatingCostsSheet.length > 1) {
        // 获取第二行及以后的数据，包含规则
        const brandManagementFeeRules: CostRule[] = [];
        
        // 解析水电费规则
        if (operatingCostsSheet[1]['水电费'] && typeof operatingCostsSheet[1]['水电费'] === 'string') {
          const waterElectricityRule = parseCostLimitRule(operatingCostsSheet[1]['水电费']);
          if (waterElectricityRule) {
            costLimits.waterElectricity = waterElectricityRule;
          }
        }
        
        // 解析门店其他费用规则
        if (operatingCostsSheet[1]['门店其他费用'] && typeof operatingCostsSheet[1]['门店其他费用'] === 'string') {
          costLimits.otherStoreCosts = {
            rules: operatingCostsSheet[1]['门店其他费用']
          };
        }
        
        // 解析品牌管理费规则（可能有多行）
        for (let i = 1; i < operatingCostsSheet.length; i++) {
          const row = operatingCostsSheet[i];
          if (row['品牌管理费'] && typeof row['品牌管理费'] === 'string') {
            const rule = parseBrandManagementFeeRule(row['品牌管理费']);
            if (rule) {
              brandManagementFeeRules.push(rule);
            }
          }
        }
        
        if (brandManagementFeeRules.length > 0) {
          costLimits.brandManagementFee = {
            rules: brandManagementFeeRules
          };
        }
        
        result.operatingCostLimits = costLimits;
        console.log('成功读取费用限制规则:', costLimits);
      }
      
      console.log('成功读取月度营运费用数据:', costRatios);
    } else {
      console.warn('未找到月度营运费用工作表或数据为空');
    }
    
    // 提取建店成本信息（如果有）
    if (excelData['建店成本'] && excelData['建店成本'].length > 0) {
      const initialCostsSheet = excelData['建店成本'];
      const decorationCostRules: DecorationCostRule[] = [];
      
      // 第一行包含固定费用
      const firstRow = initialCostsSheet[0];
      
      // 提取固定费用
      result.initialCostsData = {
        franchiseFee: firstRow['加盟费'] || 0,
        brandUsageFee: firstRow['品牌使用费'] || 0,
        designFee: firstRow['门店装修设计费'] || 0,
        storeServiceFee: firstRow['建店服务费'] || 0,
        equipmentCost: firstRow['设备成本'] || 0,
        decorationCostRules: [],
        decorationCostMaxLimit: firstRow['装修成本上限'] || undefined,
        decorationCostMinLimit: firstRow['装修成本下限'] || undefined
      };
      
      // 提取装修成本规则
      initialCostsSheet.forEach((row: any) => {
        if (row['门店装修成本'] && typeof row['门店装修成本'] === 'string') {
          const rule = parseDecorationCostRule(row['门店装修成本']);
          if (rule) {
            decorationCostRules.push(rule);
          }
        }
      });
      
      // 按最小面积排序规则
      decorationCostRules.sort((a, b) => a.minArea - b.minArea);
      result.initialCostsData.decorationCostRules = decorationCostRules;
      
      console.log('成功读取建店成本数据:', maskSensitiveData(result.initialCostsData));
    } else {
      console.warn('未找到建店成本工作表或数据为空');
    }
    
    // 提取人力成本计算规则（如果有）
    if (excelData['人力成本计算'] && excelData['人力成本计算'].length > 0) {
      const laborCostSheet = excelData['人力成本计算'];
      const laborCostRules: LaborCostRule[] = [];
      
      // 遍历人力成本计算表中的每一行
      laborCostSheet.forEach((row: any) => {
        if (row['城市梯队'] && typeof row['城市梯队'] === 'string') {
          const rule: LaborCostRule = {
            cityTier: row['城市梯队'],
            minValue: row['最小值'] || 0,
            averageSalary: row['人均工资'] || 0,
            thresholdRevenue: row['起步业绩'] || 0,
            thresholdLabor: row['起步人工'] || 0,
            increaseRate: (row['增加费率'] || 0) 
          };
          
          laborCostRules.push(rule);
        }
      });
      
      result.laborCostRules = laborCostRules;
      console.log('成功读取人力成本计算规则:', laborCostRules);
    } else {
      console.warn('未找到人力成本计算工作表或数据为空');
    }
    
    return result;
  } catch (error) {
    console.error('提取数据时出错:', error);
    return result;
  }
}

export interface CalculatedOperatingCosts {
  waterElectricity: number;
  otherStoreCosts: number;
  brandManagementFee: number;
  laborCosts: number;
}

export const calculateOperatingCosts = (
  estimatedRevenue: number,
  cityTier: string,
  operatingCostRatios: Record<string, OperatingCostRatios> | undefined,
  operatingCostLimits: OperatingCostLimits | undefined,
  laborCostRules: LaborCostRule[] | undefined
): CalculatedOperatingCosts => {
  const defaultResult: CalculatedOperatingCosts = {
    waterElectricity: 0,
    otherStoreCosts: 0,
    brandManagementFee: 0,
    laborCosts: 0
  };

  if (estimatedRevenue <= 0 || !operatingCostRatios || !operatingCostRatios['default']) {
    return defaultResult;
  }

  const ratios = operatingCostRatios['default'];

  let waterElectricity = Math.round(estimatedRevenue * ratios.waterElectricityRatio);
  const otherStoreCosts = Math.round(estimatedRevenue * ratios.otherStoreCostsRatio);

  let brandManagementFee = 0;
  if (operatingCostLimits?.brandManagementFee?.rules && operatingCostLimits.brandManagementFee.rules.length > 0) {
    brandManagementFee = calculateBrandManagementFee(estimatedRevenue, operatingCostLimits.brandManagementFee.rules);
  } else {
    brandManagementFee = Math.round(estimatedRevenue * ratios.brandManagementFeeRatio);
  }

  let laborCosts = 0;
  if (laborCostRules && laborCostRules.length > 0 && cityTier) {
    laborCosts = calculateLaborCost(estimatedRevenue, cityTier, laborCostRules);
  } else {
    laborCosts = Math.round(estimatedRevenue * ratios.laborCostsRatio);
  }

  const waterElectricityLimits = operatingCostLimits?.waterElectricity;
  if (waterElectricityLimits) {
    if (waterElectricityLimits.min !== undefined && waterElectricity < waterElectricityLimits.min) {
      waterElectricity = waterElectricityLimits.min;
    } else if (waterElectricityLimits.max !== undefined && waterElectricity > waterElectricityLimits.max) {
      waterElectricity = waterElectricityLimits.max;
    }
  }

  return { waterElectricity, otherStoreCosts, brandManagementFee, laborCosts };
};

/**
 * 计算装修成本
 * @param area 面积
 * @param rules 装修成本规则
 * @param maxLimit 装修成本上限（可选）
 * @param minLimit 装修成本下限（可选）
 * @returns 装修成本
 */
export const calculateDecorationCost = (
  area: number,
  rules: DecorationCostRule[],
  maxLimit?: number,
  minLimit?: number
): number => {
  if (!rules || rules.length === 0 || area <= 0) {
    return 0;
  }

  let decorationCost = 0;

  // 根据面积和规则计算基础装修成本
  for (const rule of rules) {
    if (area >= rule.minArea && (rule.maxArea === null || area < rule.maxArea)) {
      decorationCost = Math.round(area * rule.costPerSquareMeter);
      break;
    }
  }

  // 如果没有匹配的规则，使用最后一个规则（通常是最大面积的规则）
  if (decorationCost === 0 && rules.length > 0) {
    const lastRule = rules[rules.length - 1];
    decorationCost = Math.round(area * lastRule.costPerSquareMeter);
  }

  // 应用上下限约束
  if (maxLimit !== undefined && decorationCost > maxLimit) {
    decorationCost = maxLimit;
  }

  if (minLimit !== undefined && decorationCost < minLimit) {
    decorationCost = minLimit;
  }

  return decorationCost;
};

/**
 * 计算36个月内收回全部投资所需的最低月营业额
 * @param initialCosts 建店成本
 * @param operatingCostsWithoutRent 月度运营费用（不包括租金）- 仅用于初始估算
 * @param businessArea 商圈类型（用于确定毛利率）
 * @param rent 月租金
 * @param laborCostRules 人力成本计算规则
 * @param brandManagementFeeRules 品牌管理费规则
 * @param cityTier 城市梯队
 * @param operatingCostRatios 运营成本比例
 * @param operatingCostLimits 运营成本限制
 * @returns 36个月内收回全部投资所需的最低月营业额
 */
export const calculateMinRevenueFor36MonthPayback = (
  initialCosts: number,
  operatingCostsWithoutRent: number,
  businessArea: string,
  rent: number,
  laborCostRules?: LaborCostRule[],
  brandManagementFeeRules?: CostRule[],
  cityTier?: string,
  operatingCostRatios?: Record<string, OperatingCostRatios>,
  operatingCostLimits?: OperatingCostLimits
): number => {
  // 根据商圈类型获取毛利率
  const grossMarginRate = businessArea ? getGrossMarginRateByBusinessArea(businessArea) : 0.6;
  
  // 计算36个月内收回投资所需的每月最低利润
  const requiredMonthlyProfit = initialCosts / 36;
  
  // 初始估算：使用简化公式得到一个初始值
  let estimatedRevenue = (requiredMonthlyProfit + operatingCostsWithoutRent + rent) / grossMarginRate;
  
  // 使用迭代法逼近实际所需营业额，考虑到运营成本会随营业额变化
  // 最多迭代10次，通常3-5次就能收敛到足够精确的结果
  const MAX_ITERATIONS = 10;
  const CONVERGENCE_THRESHOLD = 100; // 两次迭代结果相差小于100元认为已收敛
  
  for (let i = 0; i < MAX_ITERATIONS; i++) {
    // 计算在当前估计营业额下的各项运营成本
    let currentWaterElectricity = 0;
    let currentOtherStoreCosts = 0;
    let currentBrandManagementFee = 0;
    let currentLaborCosts = 0;
    
    // 1. 水电费
    if (operatingCostRatios?.default?.waterElectricityRatio) {
      currentWaterElectricity = Math.round(estimatedRevenue * operatingCostRatios.default.waterElectricityRatio);
      
      // 应用水电费的最低值和最高值限制
      if (operatingCostLimits?.waterElectricity) {
        if (operatingCostLimits.waterElectricity.min !== undefined && currentWaterElectricity < operatingCostLimits.waterElectricity.min) {
          currentWaterElectricity = operatingCostLimits.waterElectricity.min;
        } else if (operatingCostLimits.waterElectricity.max !== undefined && currentWaterElectricity > operatingCostLimits.waterElectricity.max) {
          currentWaterElectricity = operatingCostLimits.waterElectricity.max;
        }
      }
    }
    
    // 2. 门店其他费用
    if (operatingCostRatios?.default?.otherStoreCostsRatio) {
      currentOtherStoreCosts = Math.round(estimatedRevenue * operatingCostRatios.default.otherStoreCostsRatio);
    }
    
    // 3. 品牌管理费
    if (brandManagementFeeRules && brandManagementFeeRules.length > 0) {
      currentBrandManagementFee = calculateBrandManagementFee(estimatedRevenue, brandManagementFeeRules);
    } else if (operatingCostRatios?.default?.brandManagementFeeRatio) {
      currentBrandManagementFee = Math.round(estimatedRevenue * operatingCostRatios.default.brandManagementFeeRatio);
    }
    
    // 4. 人工费用
    if (laborCostRules && laborCostRules.length > 0 && cityTier) {
      currentLaborCosts = calculateLaborCost(estimatedRevenue, cityTier, laborCostRules);
    } else if (operatingCostRatios?.default?.laborCostsRatio) {
      currentLaborCosts = Math.round(estimatedRevenue * operatingCostRatios.default.laborCostsRatio);
    }
    
    // 计算当前总运营成本（不含租金）
    const currentOperatingCostsWithoutRent = currentWaterElectricity + currentOtherStoreCosts + 
                                             currentBrandManagementFee + currentLaborCosts;
    
    // 根据当前运营成本，重新计算所需营业额
    const newEstimatedRevenue = (requiredMonthlyProfit + currentOperatingCostsWithoutRent + rent) / grossMarginRate;
    
    // 检查是否收敛
    if (Math.abs(newEstimatedRevenue - estimatedRevenue) < CONVERGENCE_THRESHOLD) {
      estimatedRevenue = newEstimatedRevenue;
      break;
    }
    
    // 更新估计值，继续迭代
    estimatedRevenue = newEstimatedRevenue;
  }
  
  return Math.round(estimatedRevenue);
};

/**
 * 计算测算结果
 * @param basicInfo 基础信息
 * @param operatingCosts 月度运营费用
 * @param initialCosts 建店成本
 * @param filteredData 筛选后的数据（包含费用规则等）
 * @returns 测算结果
 */
export const calculateResults = (
  basicInfo: BasicInfo,
  operatingCosts: OperatingCosts,
  initialCosts: InitialCosts,
  filteredData?: FilteredData
): CalculationResults => {
  // 实际使用的预估月营业额
  const actualEstimatedRevenue = basicInfo.estimatedRevenue;
  
  // 根据商圈类型获取毛利率
  const grossMarginRate = basicInfo.businessArea ? 
    getGrossMarginRateByBusinessArea(basicInfo.businessArea) : 0.6;
  
  // 预估毛利 = 预估月营业额 × 毛利率
  const estimatedGrossMargin = actualEstimatedRevenue * grossMarginRate;
  
  // 月均利润（不含折旧）= 预估毛利 - 月度运营费用合计
  const monthlyProfitNoDepreciation = estimatedGrossMargin - operatingCosts.total;
  
  // 月均利润（含折旧）= 月均利润（不含折旧）- 月折旧费用
  const monthlyProfitWithDepreciation = monthlyProfitNoDepreciation - (initialCosts.total / 36);
  
  // 回本周期（月）= 建店成本合计 ÷ 月均利润（不含折旧）
  const paybackPeriod = monthlyProfitNoDepreciation > 0 
    ? initialCosts.total / monthlyProfitNoDepreciation 
    : UNRECOVERABLE_PAYBACK_PERIOD;
  
  // 36个月回本月租金 = 预估毛利 - （月度运营费用合计 - 合计租金） - (建店成本合计/36)
  const rent36Month = estimatedGrossMargin - (operatingCosts.total - operatingCosts.rent) - (initialCosts.total / 36);
  
  // 计算36个月内收回全部投资所需的最低月营业额
  const operatingCostsWithoutRent = operatingCosts.total - operatingCosts.rent;
  
  // 从filteredData中提取所需的规则
  let laborCostRules;
  let brandManagementFeeRules;
  let operatingCostRatios;
  let operatingCostLimits;
  
  if (filteredData) {
    laborCostRules = filteredData.laborCostRules;
    operatingCostRatios = filteredData.operatingCostRatios;
    operatingCostLimits = filteredData.operatingCostLimits;
    
    if (operatingCostLimits?.brandManagementFee?.rules) {
      brandManagementFeeRules = operatingCostLimits.brandManagementFee.rules;
    }
  }
  
  const minRevenueFor36MonthPayback = calculateMinRevenueFor36MonthPayback(
    initialCosts.total,
    operatingCostsWithoutRent,
    basicInfo.businessArea,
    operatingCosts.rent,
    laborCostRules,
    brandManagementFeeRules,
    basicInfo.cityTier,
    operatingCostRatios,
    operatingCostLimits
  );
  
  return {
    actualEstimatedRevenue,
    estimatedGrossMargin,
    monthlyProfitNoDepreciation,
    monthlyProfitWithDepreciation,
    paybackPeriod,
    rent36Month,
    minRevenueFor36MonthPayback
  };
};

/**
 * 计算品牌管理费
 * @param revenue 营业额
 * @param rules 品牌管理费规则
 * @returns 计算后的品牌管理费
 */
export const calculateBrandManagementFee = (revenue: number, rules: CostRule[]): number => {
  if (!rules || rules.length === 0 || revenue <= 0) {
    return 0;
  }
  
  // 根据营业额匹配适用的规则
  for (const rule of rules) {
    const minMatch = rule.minRevenue === undefined || revenue >= rule.minRevenue;
    const maxMatch = rule.maxRevenue === undefined || revenue < rule.maxRevenue;
    
    if (minMatch && maxMatch) {
      // 固定金额
      if (rule.fixedAmount !== undefined && rule.percentage === undefined && rule.excessPercentage === undefined) {
        return rule.fixedAmount;
      }
      // 百分比计算
      else if (rule.percentage !== undefined) {
        return Math.round(revenue * rule.percentage);
      }
      // 阶梯计算（固定金额+超出部分*百分比）
      else if (rule.fixedAmount !== undefined && rule.thresholdAmount !== undefined && rule.excessPercentage !== undefined) {
        const excessAmount = Math.max(0, revenue - rule.thresholdAmount);
        return Math.round(rule.fixedAmount + excessAmount * rule.excessPercentage);
      }
    }
  }
  
  // 如果没有匹配的规则，返回0
  return 0;
};

/**
 * 计算人工费用
 * @param revenue 预估月营业额
 * @param cityTier 城市梯队
 * @param rules 人力成本计算规则
 * @returns 计算后的人工费用
 */
export const calculateLaborCost = (revenue: number, cityTier: string, rules: LaborCostRule[]): number => {
  if (!rules || rules.length === 0 || revenue <= 0 || !cityTier) {
    return 0;
  }
  
  // 查找对应城市梯队的规则
  const rule = rules.find(r => r.cityTier === cityTier);
  if (!rule) {
    return 0;
  }
  
  // 按照规则计算人工费用
  let laborCost = 0;
  
  if (revenue <= 130000) {
    // 如果月营业额小于等于13万，直接使用最小值
    laborCost = rule.minValue;
  } else if (revenue < rule.thresholdRevenue) {
    // 如果月营业额大于13万但小于起步业绩，使用公式：最小值 + 向上取整((营业额-130000)*12/365/2000) * 人均工资
    const additionalStaff = Math.ceil((revenue - 130000) * 12 / 365 / 2000);
    laborCost = rule.minValue + additionalStaff * rule.averageSalary;
  } else {
    // 如果营业额 > 130000 且 >= 起步业绩，使用公式：起步人工 + (营业额-起步业绩) * 增加费率
    laborCost = rule.thresholdLabor + (revenue - rule.thresholdRevenue) * rule.increaseRate;
  }
  
  // 确保人工费用是整数
  return Math.round(laborCost);
}; 